import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditorRef } from 'platejs/react';
import { FindReplacePlugin } from '@platejs/find-replace';
import styles from './FindReplacePanel.module.scss';

export interface FindReplacePanelHandle {
    open: () => void;
    close: () => void;
    toggle: () => void;
}

// --------------------------------------------------------------------------
// Match scanning — mirrors the flat-offset logic inside decorateFindReplace
// --------------------------------------------------------------------------

/**
 * Collect the Slate range for every occurrence of `search` in the editor tree.
 * Only scans element nodes whose direct children are all text leaves — the same
 * constraint used by the plugin's own `decorateFindReplace` so our count stays
 * in sync with the number of highlighted spans.
 */
export function collectMatchRanges(editor: any, search: string): any[] {
    if (!search || !editor?.children) return [];
    const searchLower = search.toLowerCase();
    const ranges: any[] = [];

    const visitNode = (node: any, path: number[]) => {
        if (!node || typeof node.text === 'string') return;
        if (!Array.isArray(node.children)) return;

        if (node.children.every((c: any) => typeof c.text === 'string')) {
            // Leaf-bearing element — scan for matches.
            const texts: string[] = node.children.map((c: any) => c.text);
            const joined = texts.join('').toLowerCase();
            let start = 0;
            while (true) {
                const idx = joined.indexOf(searchLower, start);
                if (idx === -1) break;
                const matchEnd = idx + search.length;
                // Map flat char offsets back to leaf-level anchor / focus.
                let cum = 0;
                let anchorLeaf = 0;
                let anchorOffset = 0;
                for (let ti = 0; ti < texts.length; ti++) {
                    const leafEnd = cum + texts[ti].length;
                    if (idx < leafEnd) {
                        anchorLeaf = ti;
                        anchorOffset = idx - cum;
                        break;
                    }
                    cum = leafEnd;
                }
                cum = 0;
                let focusLeaf = 0;
                let focusOffset = 0;
                for (let ti = 0; ti < texts.length; ti++) {
                    const leafEnd = cum + texts[ti].length;
                    if (matchEnd <= leafEnd) {
                        focusLeaf = ti;
                        focusOffset = matchEnd - cum;
                        break;
                    }
                    cum = leafEnd;
                }
                ranges.push({
                    anchor: { path: [...path, anchorLeaf], offset: anchorOffset },
                    focus: { path: [...path, focusLeaf], offset: focusOffset },
                });
                start = idx + search.length;
            }
        } else {
            node.children.forEach((child: any, i: number) => visitNode(child, [...path, i]));
        }
    };

    (editor.children as any[]).forEach((node: any, i: number) => visitNode(node, [i]));
    return ranges;
}

/** Scroll a match range's start node into view. */
function scrollRangeIntoView(editor: any, range: any) {
    try {
        const domRange = editor.toDOMRange?.(range);
        if (!domRange) return;
        const el = domRange.startContainer.parentElement as HTMLElement | null;
        el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    } catch {
        // non-fatal — editor might not be mounted
    }
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export const FindReplacePanel = forwardRef<FindReplacePanelHandle>(function FindReplacePanel(_props, ref) {
    const editor = useEditorRef() as any;

    const [isOpen, setIsOpen] = useState(false);
    const [find, setFind] = useState('');
    const [replace, setReplace] = useState('');
    const [currentIdx, setCurrentIdx] = useState(0);

    const findInputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

    // Keep the panel pinned to the editor's top-right in the VIEWPORT (position:
    // fixed), recomputed on scroll/resize. The panel lives inside the editor
    // shell, which in some surfaces (compose-email modal body, doc drawer) is
    // itself a scroll container — so a position:absolute panel scrolled away with
    // the content. Anchoring to the shell's live rect keeps it visible while any
    // ancestor scrolls, and tied to the editor (not floating at a screen corner).
    useLayoutEffect(() => {
        if (!isOpen) return undefined;
        const update = () => {
            const shell = panelRef.current?.closest('[data-testid="plate-editor-root"]') as HTMLElement | null;
            if (!shell) return;
            const r = shell.getBoundingClientRect();
            setPos({
                top: Math.max(8, r.top + 44), // clears the toolbar; clamps into the viewport when scrolled
                right: Math.max(8, window.innerWidth - r.right + 12),
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isOpen]);

    // Push the search string into FindReplacePlugin so its decorate fires.
    // When closed, clear to '' so no highlights remain in the DOM.
    useEffect(() => {
        (editor as any).setOption?.(FindReplacePlugin, 'search', isOpen ? find : '');
        // setOption alone does NOT repaint matches: Plate caches decorations and
        // only re-runs `decorate` when versionDecorate increments via redecorate().
        (editor as any).api?.redecorate?.();
    }, [editor, find, isOpen]);

    const close = useCallback(() => {
        setIsOpen(false);
        // Clear highlights immediately.
        (editor as any).setOption?.(FindReplacePlugin, 'search', '');
        (editor as any).api?.redecorate?.();
        setFind('');
        setReplace('');
        setCurrentIdx(0);
    }, [editor]);

    const open = useCallback(() => {
        setIsOpen(true);
        setCurrentIdx(0);
        requestAnimationFrame(() => {
            findInputRef.current?.focus();
            findInputRef.current?.select();
        });
    }, []);

    const toggle = useCallback(() => {
        if (isOpen) close();
        else open();
    }, [isOpen, open, close]);

    useImperativeHandle(ref, () => ({ open, close, toggle }), [open, close, toggle]);

    // Expose this panel's toggle on the OWNING editor instance so the toolbar's
    // FindReplaceButton can target only this editor. A document-global event
    // would fan out to every co-mounted editor's panel (one-pager + its
    // comments thread, compose-email modal over a doc), opening all of them
    // and stealing focus. `useEditorRef()` resolves to the editor that owns
    // the Plate context this panel renders in, so the binding is per-instance.
    useEffect(() => {
        editor.__findReplaceToggle = toggle;
        return () => {
            if (editor.__findReplaceToggle === toggle) editor.__findReplaceToggle = undefined;
        };
    }, [editor, toggle]);

    // Memoize match scanning: collectMatchRanges walks the whole document.
    // Key on `find` + `editor.children` (Slate replaces the array reference on
    // every edit) so we recompute once per (query, document version) rather than
    // on every render / keystroke.
    const matches = useMemo(
        () => collectMatchRanges(editor, find),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editor, find, editor.children],
    );

    // Clamp currentIdx whenever the match count changes (user typing).
    const matchCount = matches.length;
    useEffect(() => {
        setCurrentIdx((prev) => (matchCount === 0 ? 0 : Math.min(prev, matchCount - 1)));
    }, [matchCount]);

    const goTo = useCallback(
        (idx: number) => {
            if (!matches.length) return;
            const clamped = ((idx % matches.length) + matches.length) % matches.length;
            setCurrentIdx(clamped);
            scrollRangeIntoView(editor, matches[clamped]);
        },
        [editor, matches],
    );

    const handleFindKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                goTo(currentIdx + 1);
            } else if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                goTo(currentIdx - 1);
            }
        },
        [close, goTo, currentIdx],
    );

    const handleReplaceKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close();
            }
        },
        [close],
    );

    const doReplace = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (!matches.length) return;
            const target = matches[currentIdx];
            editor.tf.withoutNormalizing(() => {
                editor.tf.select(target);
                editor.tf.insertText(replace);
            });
            // Repaint: the remaining matches shifted, so refresh the decorations.
            (editor as any).api?.redecorate?.();
            // Stay at the same index — it now points at what was the next match.
        },
        [editor, matches, currentIdx, replace],
    );

    const doReplaceAll = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (!matches.length) return;
            // Replace in reverse order so earlier offsets remain valid.
            editor.tf.withoutNormalizing(() => {
                for (let i = matches.length - 1; i >= 0; i--) {
                    editor.tf.select(matches[i]);
                    editor.tf.insertText(replace);
                }
            });
            (editor as any).api?.redecorate?.();
            setCurrentIdx(0);
        },
        [editor, matches, replace],
    );

    if (!isOpen) return null;

    const total = matches.length;
    const displayIdx = total > 0 ? currentIdx + 1 : 0;

    return (
        <div
            ref={panelRef}
            className={styles.panel}
            style={pos ? { position: 'fixed', top: pos.top, right: pos.right } : undefined}
            role="search"
            aria-label="Find and replace"
        >
            <div className={styles.row}>
                <div className={styles.inputWrap}>
                    <input
                        ref={findInputRef}
                        className={styles.input}
                        type="text"
                        placeholder="Find…"
                        value={find}
                        onChange={(e) => setFind(e.target.value)}
                        onKeyDown={handleFindKeyDown}
                        aria-label="Find"
                        spellCheck={false}
                    />
                    {find.length > 0 && (
                        <span className={styles.matchCount} aria-live="polite">
                            {total === 0 ? 'No matches' : `${displayIdx} / ${total}`}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    className={styles.navBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        goTo(currentIdx - 1);
                    }}
                    disabled={total === 0}
                    title="Previous match (Shift+Enter)"
                    aria-label="Previous match"
                >
                    ▲
                </button>
                <button
                    type="button"
                    className={styles.navBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        goTo(currentIdx + 1);
                    }}
                    disabled={total === 0}
                    title="Next match (Enter)"
                    aria-label="Next match"
                >
                    ▼
                </button>
                <button
                    type="button"
                    className={styles.closeBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        close();
                    }}
                    title="Close (Escape)"
                    aria-label="Close find and replace"
                >
                    ✕
                </button>
            </div>
            <div className={styles.row}>
                <input
                    className={styles.input}
                    type="text"
                    placeholder="Replace…"
                    value={replace}
                    onChange={(e) => setReplace(e.target.value)}
                    onKeyDown={handleReplaceKeyDown}
                    aria-label="Replace"
                    spellCheck={false}
                />
                <button
                    type="button"
                    className={styles.actionBtn}
                    onMouseDown={doReplace}
                    disabled={total === 0}
                    title="Replace current match"
                    aria-label="Replace"
                >
                    Replace
                </button>
                <button
                    type="button"
                    className={styles.actionBtn}
                    onMouseDown={doReplaceAll}
                    disabled={total === 0}
                    title="Replace all matches"
                    aria-label="Replace all"
                >
                    All
                </button>
            </div>
        </div>
    );
});
